"use client";

import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  PackageX,
  TrendingDown,
  Users,
  MessageCircleX,
  Handshake,
  Bot,
  Clock,
  DollarSign,
  ShoppingCart,
  UserX,
  LucideIcon,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";

interface Problem {
  title: string;
  pain: string;
  icon: LucideIcon;
  color: string;
  solution: "buddify" | "ai";
}

// Row 1 - Mixed problems (scrolls left)
const problemsRow1: Problem[] = [
  {
    title: "Limited Product Catalog",
    pain: "Customers leave when they can't find what they need",
    icon: PackageX,
    color: "red",
    solution: "buddify",
  },
  {
    title: "Visitors Leave Empty-Handed",
    pain: "70% of shoppers abandon their carts without buying",
    icon: TrendingDown,
    color: "purple",
    solution: "ai",
  },
  {
    title: "Competing Alone",
    pain: "Small stores struggle against big retailers",
    icon: Users,
    color: "orange",
    solution: "buddify",
  },
  {
    title: "No Real-Time Engagement",
    pain: "Customers have questions but no one's there to answer",
    icon: MessageCircleX,
    color: "violet",
    solution: "ai",
  },
  {
    title: "Missed B2B Revenue",
    pain: "Other businesses want your products but can't buy in bulk",
    icon: DollarSign,
    color: "amber",
    solution: "buddify",
  },
  {
    title: "Can't Be There 24/7",
    pain: "Sales happen around the clock but you need to sleep",
    icon: Clock,
    color: "indigo",
    solution: "ai",
  },
];

// Row 2 - Mixed problems (scrolls right)
const problemsRow2: Problem[] = [
  {
    title: "No Network Effect",
    pain: "Your store exists in isolation without cross-promotion",
    icon: ShoppingCart,
    color: "yellow",
    solution: "buddify",
  },
  {
    title: "Price-Sensitive Shoppers",
    pain: "Customers want deals but you have no way to negotiate",
    icon: UserX,
    color: "blue",
    solution: "ai",
  },
  {
    title: "Limited Product Catalog",
    pain: "Customers leave when they can't find what they need",
    icon: PackageX,
    color: "red",
    solution: "buddify",
  },
  {
    title: "Visitors Leave Empty-Handed",
    pain: "70% of shoppers abandon their carts without buying",
    icon: TrendingDown,
    color: "purple",
    solution: "ai",
  },
  {
    title: "Competing Alone",
    pain: "Small stores struggle against big retailers",
    icon: Users,
    color: "orange",
    solution: "buddify",
  },
  {
    title: "No Real-Time Engagement",
    pain: "Customers have questions but no one's there to answer",
    icon: MessageCircleX,
    color: "violet",
    solution: "ai",
  },
];

const colorClasses: Record<
  string,
  { bg: string; text: string; border: string; iconBg: string }
> = {
  red: {
    bg: "from-red-500/10 via-red-400/5 to-transparent",
    text: "text-red-600",
    border: "border-red-200/50",
    iconBg: "bg-red-100/80",
  },
  orange: {
    bg: "from-orange-500/10 via-orange-400/5 to-transparent",
    text: "text-orange-600",
    border: "border-orange-200/50",
    iconBg: "bg-orange-100/80",
  },
  amber: {
    bg: "from-amber-500/10 via-amber-400/5 to-transparent",
    text: "text-amber-600",
    border: "border-amber-200/50",
    iconBg: "bg-amber-100/80",
  },
  yellow: {
    bg: "from-yellow-500/10 via-yellow-400/5 to-transparent",
    text: "text-yellow-600",
    border: "border-yellow-200/50",
    iconBg: "bg-yellow-100/80",
  },
  purple: {
    bg: "from-purple-500/10 via-purple-400/5 to-transparent",
    text: "text-purple-600",
    border: "border-purple-200/50",
    iconBg: "bg-purple-100/80",
  },
  violet: {
    bg: "from-violet-500/10 via-violet-400/5 to-transparent",
    text: "text-violet-600",
    border: "border-violet-200/50",
    iconBg: "bg-violet-100/80",
  },
  indigo: {
    bg: "from-indigo-500/10 via-indigo-400/5 to-transparent",
    text: "text-indigo-600",
    border: "border-indigo-200/50",
    iconBg: "bg-indigo-100/80",
  },
  blue: {
    bg: "from-blue-500/10 via-blue-400/5 to-transparent",
    text: "text-blue-600",
    border: "border-blue-200/50",
    iconBg: "bg-blue-100/80",
  },
};

function ProblemCard({ problem }: { problem: Problem }) {
  const colors = colorClasses[problem.color];
  const ProblemIcon = problem.icon;
  const SolutionIcon = problem.solution === "buddify" ? Handshake : Bot;
  const solutionLabel =
    problem.solution === "buddify"
      ? "Brand Partnerships"
      : "Buddy AI Assistant";
  const solutionColor =
    problem.solution === "buddify" ? "primary" : "secondary";

  return (
    <div
      className={`flex-shrink-0 w-[300px] sm:w-[360px] relative p-4 sm:p-5 rounded-2xl border ${colors.border} overflow-hidden shadow-lg backdrop-blur-md bg-white/80 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] ring-1 ring-transparent hover:ring-white/50`}
    >
      {/* Gradient overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${colors.bg} pointer-events-none`}
      />

      {/* Frosted glass texture */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/40 via-transparent to-transparent pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">
        {/* Problem Icon & Title */}
        <div className="flex items-start gap-3 mb-3">
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colors.iconBg} backdrop-blur-sm flex items-center justify-center shrink-0 shadow-sm border border-white/50`}
          >
            <ProblemIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900 text-sm sm:text-base leading-tight">
              {problem.title}
            </h3>
            <p className="text-xs sm:text-sm text-stone-600 mt-0.5 line-clamp-2">
              {problem.pain}
            </p>
          </div>
        </div>

        {/* Solution label */}
        <div
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg backdrop-blur-sm ${
            solutionColor === "primary"
              ? "bg-primary-500/15 border border-primary-200/50"
              : "bg-secondary-500/15 border border-secondary-200/50"
          }`}
        >
          <span className="text-xs text-stone-500 font-medium">
            Solve with:
          </span>
          <SolutionIcon
            className={`w-3.5 h-3.5 ${solutionColor === "primary" ? "text-primary-600" : "text-secondary-600"}`}
          />
          <span
            className={`text-xs font-semibold ${solutionColor === "primary" ? "text-primary-700" : "text-secondary-700"}`}
          >
            {solutionLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

function CarouselRow({
  problems,
  direction,
  delay = 0,
  isPaused,
}: {
  problems: Problem[];
  direction: "left" | "right";
  delay?: number;
  isPaused: boolean;
}) {
  // Double the problems for seamless infinite scroll
  const duplicatedProblems = [...problems, ...problems];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="relative overflow-hidden py-5"
    >
      <div
        className={`flex gap-4 ${
          direction === "left"
            ? "animate-scroll-left-slow"
            : "animate-scroll-right-slow"
        }`}
        style={{
          animationPlayState: isPaused ? "paused" : "running",
        }}
      >
        {duplicatedProblems.map((problem, index) => (
          <ProblemCard key={`${problem.title}-${index}`} problem={problem} />
        ))}
      </div>
    </motion.div>
  );
}

export function ProblemSection() {
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    startX.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setTimeout(() => setIsPaused(false), 150);
    }
  }, [isDragging]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    startX.current = e.touches[0].pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.touches[0].pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  return (
    <Section background="white">
      <Container>
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-display-1 text-stone-900 mb-4">
            Growing an E-Commerce
            <br />
            business is <span className="text-red-500">hard</span>
          </h2>
          <p className="text-body-lg max-w-3xl mx-auto text-stone-600">
            You&apos;re facing challenges that cost you sales{" "}
            <span className="font-semibold text-stone-900">
              every single day
            </span>
            .
          </p>
        </motion.div>
      </Container>

      {/* Full-width carousel area */}
      <div className="relative">
        {/* Gradient fade on edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        {/* Draggable carousel container */}
        <div
          ref={containerRef}
          className={`draggable-carousel ${isDragging ? "is-dragging" : ""}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Carousel rows */}
          <div className="flex flex-col gap-0">
            <CarouselRow
              problems={problemsRow1}
              direction="left"
              delay={0.1}
              isPaused={isPaused}
            />
            <CarouselRow
              problems={problemsRow2}
              direction="right"
              delay={0.2}
              isPaused={isPaused}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}
